import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { pointOnFeature } from '@turf/turf';
import classNames from 'classnames';
import { map, sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { addToken } from '~/api/layers2';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import getConfig from '~/config';
import { useTabControlledLayers } from '~/store2/tabLayerLocks';
import store from './store';
import ZoningDetails from './ZoningDetails';

const config = getConfig();
const PARCELS_LAYER_ID = 'landRightsParcels';
// Stable identity — useTabControlledLayers re-locks on identity change.
const TAB_CONTROLLED_LAYER_IDS = [PARCELS_LAYER_ID];
const ZONING_MIN_ZOOM = 15;

export function TabDetails() {
  const { zonesInView, highlightedZoneCode, mapZoom } = useSnapshot(store);

  const el = useMemo(() => {
    if (Object.keys(zonesInView).length === 0) {
      if (mapZoom < ZONING_MIN_ZOOM) {
        return <div>Zoom in to see detailed zoning information.</div>;
      }
      return <SkeletonSpinnerCentered />;
    }
    const trs = map(sortBy(zonesInView, ['zone_name']), (z, k) => {
      const isSelected = z.zone_code === highlightedZoneCode;
      return (
        <tr
          onClick={() => store.updateHighlightedZoneCode(z.zone_code)}
          key={k}
          className={classNames('cursor-pointer px-1', {
            'bg-brand-light': isSelected,
          })}
        >
          <td>
            <div className="h-6 w-6 rounded-full" style={{ backgroundColor: z.fill_color }}></div>
          </td>
          <td className="p-2">
            {z.zone_name} ({z.zone_code})
            {isSelected && (
              <ZoningDetails zone={z} trigger={<span className="cursor-pointer underline">View Details</span>} />
            )}
          </td>
          <td className="pr-2">{z.zone_type}</td>
        </tr>
      );
    });

    return (
      <table className="table-auto text-left text-xs">
        <thead>
          <tr>
            <th></th>
            <th>Zone Name</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>{trs}</tbody>
      </table>
    );
  }, [zonesInView, highlightedZoneCode, mapZoom]);

  return <div>{el}</div>;
}

export function MapChildren() {
  // This tab uses left-click to select zones, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { highlightedZoneCode } = useSnapshot(store) as typeof store;

  const { current: map } = MapGL.useMap();

  async function onClick(e: MapGL.MapMouseEvent) {
    if (map) {
      const foundFeatures = map?.queryRenderedFeatures(e.point, {
        layers: ['zoning-fill'],
      }) as GeoJSON.Feature<GeoJSON.Point>[] | undefined;

      store.updateHighlightedZoneCode(foundFeatures?.[0]?.properties?.zone_code);
    }
  }

  async function onMove() {
    if (map) {
      const set: any = {};
      //@ts-expect-error
      const zonesInView = map.queryRenderedFeatures(undefined, {
        layers: ['zoning-fill'],
      });
      zonesInView.forEach((z) => {
        const point = pointOnFeature(z.geometry);
        set[z.properties!.zone_code] = {
          ...z.properties!,
          point: {
            lng: point.geometry.coordinates[0],
            lat: point.geometry.coordinates[1],
          },
        };
      });
      store.zonesInView = set;
      store.updateMapZoom(map.getZoom());
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!map) return;
    map.on('click', onClick);
    map.on('move', onMove);
    setTimeout(onMove, 1_000); // trigger first load

    store.updateMapZoom(map.getZoom());

    return () => {
      map?.off('click', onClick);
      map?.off('move', onMove);
    };
  }, [map]);

  // The tab needs parcels rendered while it is open: force it on and lock it (the picker
  // shows it checked + disabled), then return it to picker-controlled state on unmount.
  useTabControlledLayers(TAB_CONTROLLED_LAYER_IDS);

  return (
    <MapGL.Source
      id="zoningOverlay"
      type="vector"
      tiles={[addToken(`${config.tileServerBase}/v1/tiles/zoneomics/{z}/{x}/{y}.mvt`)]}
      minzoom={ZONING_MIN_ZOOM}
      maxzoom={24}
    >
      <MapGL.Layer
        id="zoning-fill"
        source="zoningOverlay"
        source-layer="all"
        type="fill"
        paint={{
          'fill-color': ['get', 'fill_color'],
          'fill-opacity': ['case', ['==', ['get', 'zone_code'], highlightedZoneCode], 0.8, 0.25],
        }}
      />
      <MapGL.Layer
        id="zoning-line"
        source="zoningOverlay"
        source-layer="all"
        type="line"
        paint={{
          'line-color': 'black',
          'line-width': ['interpolate', ['exponential', 0.5], ['zoom'], 14, 0.75, 20, 2],
        }}
      />
    </MapGL.Source>
  );
}
